/**
 * SmartStudio Config — Prashasta Smart Event Studio
 * Versioned conditional question bank + phase definitions.
 *
 * Architecture:
 *   - Questions are grouped by phase and carry a `visible(answers)` predicate
 *   - `pricingRelevant: true` triggers GQP recalculation when the answer changes
 *   - `invalidates: [id, ...]` clears dependent answers on change
 *   - Rule engine is deterministic — no AI in the question flow
 */

import {
  EVENT_TYPES, WEDDING_FUNCTIONS, CONFERENCE_TYPES,
  SERVICES, CITIES, GUEST_RANGES, VENUE_STATUSES,
  STYLE_OPTIONS, BUDGET_RANGES, END_TO_END_DEFAULTS,
} from '../../journey/journeyConfig';

export { EVENT_TYPES, SERVICES, CITIES, GUEST_RANGES, VENUE_STATUSES, STYLE_OPTIONS, BUDGET_RANGES };

// ── Schema version ───────────────────────────────────────────────────────────
export const QUESTION_ENGINE_VERSION = '1.0.0';
export const TENANT_ALIAS = 'prashasta';

// ── Phase definitions ─────────────────────────────────────────────────────────
export const PHASES = [
  { id: 'DISCOVER',    label: 'Discover',    hint: 'Tell us about your event'   },
  { id: 'PERSONALIZE', label: 'Personalise', hint: 'Make it uniquely yours'     },
  { id: 'CONFIGURE',   label: 'Configure',   hint: 'Choose your services'       },
  { id: 'REVIEW',      label: 'Review',      hint: 'See your plan & estimate'   },
  { id: 'CONNECT',     label: 'Connect',     hint: 'Let us reach out'           },
];

export const PHASE_IDS = PHASES.map(p => p.id);

// ── Empty studio state ────────────────────────────────────────────────────────
export function createEmptyStudioState() {
  return {
    session_id:      (crypto.randomUUID?.() ?? `ss-${Date.now()}`),
    started_at:      new Date().toISOString(),
    schema_version:  QUESTION_ENGINE_VERSION,
    // Phase 1 — Discover
    event_type:      null,
    location_city:   null,
    location_custom: '',
    event_date:      '',
    guest_count_range: null,
    guest_count_exact: '',
    // Phase 2 — Personalise
    wedding_functions: [],
    conference_type:   null,
    style_preference:  null,
    // Phase 3 — Configure
    planning_scope:    null,   // 'LUXURY' | 'BUILD_OWN'
    selected_services: [],
    venue_status:      null,
    budget_range:      null,
    // UX-HOME-01 — Partner Preferences
    preferred_partners: {},    // { service_code: { supplier_id, partner_name, category_id } }
    preselected_category_id: null,  // set when arriving from homepage category click
  };
}

// ── Question bank ─────────────────────────────────────────────────────────────
// Each question: { id, phase, label, subLabel?, type, required, visible(a), pricingRelevant, invalidates? }

export const QUESTIONS = [
  // ── DISCOVER ─────────────────────────────────────────────────────────────
  {
    id: 'event_type',
    phase: 'DISCOVER',
    label: 'What are you celebrating?',
    subLabel: 'Your event type shapes the entire planning experience',
    type: 'event_card_grid',
    required: true,
    visible: () => true,
    pricingRelevant: false,
    invalidates: ['wedding_functions', 'conference_type', 'style_preference'],
  },
  {
    id: 'location_city',
    phase: 'DISCOVER',
    label: 'Where will this take place?',
    subLabel: null,
    type: 'city_selector',
    required: true,
    visible: (a) => !!a.event_type,
    pricingRelevant: true,
  },
  {
    id: 'event_date',
    phase: 'DISCOVER',
    label: 'When is the event?',
    subLabel: 'Helps us check availability and seasonal pricing',
    type: 'date_input',
    required: false,
    visible: (a) => !!a.event_type,
    pricingRelevant: true,
  },
  {
    id: 'guest_count',
    phase: 'DISCOVER',
    label: 'How many guests are you expecting?',
    subLabel: null,
    type: 'guest_count_input',
    required: true,
    visible: (a) => !!a.event_type,
    pricingRelevant: true,
  },

  // ── PERSONALIZE ─────────────────────────────────────────────────────────────
  {
    id: 'wedding_functions',
    phase: 'PERSONALIZE',
    label: 'Which celebrations are you planning?',
    subLabel: 'Each can be styled and planned independently',
    type: 'multi_chip',
    required: false,
    visible: (a) => a.event_type === 'WEDDING',
    pricingRelevant: false,
    options: WEDDING_FUNCTIONS,
  },
  {
    id: 'conference_type',
    phase: 'PERSONALIZE',
    label: 'What type of event is this?',
    subLabel: null,
    type: 'single_choice',
    required: false,
    visible: (a) => ['CONFERENCE', 'CORPORATE'].includes(a.event_type),
    pricingRelevant: false,
    options: CONFERENCE_TYPES,
  },
  {
    id: 'style_preference',
    phase: 'PERSONALIZE',
    label: 'What style resonates with you?',
    subLabel: 'This guides our curated recommendations',
    type: 'style_chips',
    required: false,
    visible: (a) => ['WEDDING', 'ENGAGEMENT', 'BIRTHDAY', 'AWARDS'].includes(a.event_type),
    pricingRelevant: false,
    options: STYLE_OPTIONS,
  },
  {
    id: 'budget_range',
    phase: 'PERSONALIZE',
    label: 'What is your approximate budget?',
    subLabel: 'Helps us tailor recommendations. You can always refine this later.',
    type: 'budget_choice',
    required: false,
    visible: (a) => !!a.event_type,
    pricingRelevant: false,
    options: BUDGET_RANGES,
  },

  // ── CONFIGURE ────────────────────────────────────────────────────────────────
  {
    id: 'planning_scope',
    phase: 'CONFIGURE',
    label: 'How would you like to plan?',
    subLabel: null,
    type: 'scope_cards',
    required: true,
    visible: () => true,
    pricingRelevant: false,
    invalidates: ['selected_services'],
  },
  {
    id: 'selected_services',
    phase: 'CONFIGURE',
    label: 'Choose your services',
    subLabel: 'Select what you need — expand each for inclusions',
    type: 'service_grid',
    required: false,
    visible: (a) => a.planning_scope === 'BUILD_OWN',
    pricingRelevant: true,
  },
  {
    id: 'venue_status',
    phase: 'CONFIGURE',
    label: 'Do you have a venue in mind?',
    subLabel: null,
    type: 'venue_choice',
    required: false,
    visible: (a) => {
      if (!a.planning_scope) return false;
      if (a.planning_scope === 'LUXURY') return true;
      return (a.selected_services || []).includes('VENUE');
    },
    pricingRelevant: false,
    options: VENUE_STATUSES,
  },
];

// ── Planning scopes (luxury vs custom) ────────────────────────────────────────
export const PLANNING_SCOPES = [
  {
    value: 'LUXURY',
    label: 'Luxury Concierge',
    headline: 'We handle everything',
    desc: 'A dedicated planner curates every detail — venue, decor, catering, production and beyond.',
    badge: 'Most Popular',
    defaultServices: END_TO_END_DEFAULTS,
  },
  {
    value: 'BUILD_OWN',
    label: 'Build My Own',
    headline: 'I choose what I need',
    desc: 'Pick the specific services you want. Mix and match from our full catalog.',
    badge: null,
    defaultServices: [],
  },
];

// ── Question Engine ───────────────────────────────────────────────────────────

/** Returns the subset of QUESTIONS that should be visible given current answers. */
export function getVisibleQuestions(phase, answers) {
  return QUESTIONS.filter(q => q.phase === phase && q.visible(answers));
}

/** True when all required questions in a phase have answers. */
export function isPhaseComplete(phase, answers) {
  const required = QUESTIONS.filter(q => q.phase === phase && q.required && q.visible(answers));
  return required.every(q => {
    const val = answers[q.id === 'guest_count' ? 'guest_count_range' : q.id];
    if (Array.isArray(val)) return val.length > 0;
    return val !== null && val !== undefined && val !== '';
  });
}

/** Returns answer IDs that should be cleared when `changedId` is updated. */
export function getInvalidatedIds(changedId, answers) {
  const q = QUESTIONS.find(q => q.id === changedId);
  if (!q?.invalidates) return [];
  return q.invalidates;
}

/** True if the changed question is pricing-relevant and inputs are sufficient to call GQP. */
export function shouldRecalculate(changedId, answers) {
  const q = QUESTIONS.find(q => q.id === changedId);
  if (!q?.pricingRelevant) return false;
  // Minimum required for an estimate: event type + guest count + at least one service
  const hasGuests = !!(answers.guest_count_exact && parseInt(answers.guest_count_exact) > 0);
  const hasServices = (answers.selected_services?.length > 0) ||
                      (answers.planning_scope === 'LUXURY');
  return hasGuests && hasServices;
}

/** Returns guest count as a number, or null. */
export function resolveGuestCount(answers) {
  if (answers.guest_count_exact && parseInt(answers.guest_count_exact) > 0) {
    return parseInt(answers.guest_count_exact);
  }
  // Midpoint of range as fallback
  const RANGE_MIDPOINTS = {
    UNDER_100: 75, '100_250': 175, '250_500': 375,
    '500_750': 625, '750_1000': 875, '1000_PLUS': 1500, NOT_SURE: 300,
  };
  return RANGE_MIDPOINTS[answers.guest_count_range?.value] ?? null;
}

/** Build GQP estimate request from current studio answers and service map. */
export function buildEstimateRequest(answers, serviceMap, idempotencyKey) {
  const services = [];
  const activeServices = answers.planning_scope === 'LUXURY'
    ? END_TO_END_DEFAULTS
    : (answers.selected_services || []);

  for (const svcCode of activeServices) {
    const mapped = serviceMap?.[svcCode];
    if (mapped) {
      // service_map values may be a string (service_id directly) or an object with .service_id
      const serviceId = typeof mapped === 'string' ? mapped : mapped.service_id;
      if (serviceId) services.push({ service_id: serviceId, quantity: 1 });
    }
  }
  const req = {
    tenant_alias: TENANT_ALIAS,
    guest_count:  resolveGuestCount(answers),
    location_city: answers.location_city?.value || answers.location_custom || null,
    services,
    currency: 'INR',
    caller_idempotency_key: idempotencyKey,
  };
  // Only include event_date if set; backend requires a date, fallback to 6 months ahead
  const eventDate = answers.event_date || (() => {
    const d = new Date(); d.setMonth(d.getMonth() + 6);
    return d.toISOString().split('T')[0];
  })();
  if (eventDate) req.event_date = eventDate;
  return req;
}
